import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageHero from '../components/layout/PageHero';
import img1 from "../img/galery/18.jpeg";

const ContactsPage = () => {
  const { t } = useTranslation();

  const locations = [
    {
      id: 'amares',
      name: t('contacts.locations.amares.name'),
      address: t('contacts.locations.amares.address'),
      city: t('contacts.locations.amares.city'),
      phones: ['253 994 232', '925 460 202'],
      hours: t('contacts.locations.amares.hours'),
      mapSrc: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2982.123456789!2d-8.35!3d41.63!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sRua%20Santa%20Catarina%2036A%2C%20Ferreiros%2C%20Amares!5e0!3m2!1spt!2spt!4v1234567890',
    },
    {
      id: 'adaufe',
      name: t('contacts.locations.adaufe.name'),
      address: t('contacts.locations.adaufe.address'),
      city: t('contacts.locations.adaufe.city'),
      phones: ['253 284 121', '913 958 650'],
      hours: t('contacts.locations.adaufe.hours'),
      mapSrc: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2984.380071680482!2d-8.40764842344966!3d41.58265978385934!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd24fe7a9b56448d%3A0xf2138df50e76cc35!2zVHYuIGRvIENvbcOpcmNpbywgQWRhw7pmZQ!5e0!3m2!1sen!2spt!4v1770130783949!5m2!1sen!2spt" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade',
    },
    {
      id: 'bouro',
      name: t('contacts.locations.bouro.name'),
      address: t('contacts.locations.bouro.address'),
      city: t('contacts.locations.bouro.city'),
      phones: ['253 377 770', '935 383 101'],
      hours: t('contacts.locations.bouro.hours'),
      mapSrc: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1490.4035555316239!2d-8.272332561063132!3d41.659909396705466!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd251d0225097c87%3A0x2c2e977c7ea18a5f!2sR.%20de%20Terreiro%20142!5e0!3m2!1sen!2spt!4v1781435180090!5m2!1sen!2spt" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade',
    },
  ];

  const fadeInUp = {
    initial: { y: 30 },
    animate: { y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <PageHero
        label={t('contacts.hero.label')}
        title={t('contacts.hero.title')}
        description={t('contacts.hero.description')}
        backgroundImage={img1}
      />

      {/* Main CTA - Phone */}
      <section className="py-16 bg-charcoal">
        <div className="container-luxury">
          <motion.div
            initial={{ y: 30 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-title font-serif text-foreground mb-4">
              {t('hero.cta')}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              {t('home.cta.description')}
            </p>
            <a href="tel:+351253994232">
              <Button className="btn-luxury text-charcoal-dark px-10 py-5 h-auto text-lg font-medium">
                <Phone className="mr-3 w-5 h-5" />
                253 994 232
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Locations Grid */}
      <section className="section-luxury bg-background relative">
        <div className="container-luxury relative">
          <motion.h2
            initial={{ y: 30 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true }}
            className="text-title font-serif text-foreground text-center mb-12"
          >
            {t('contacts.info.locations')}
          </motion.h2>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {locations.map((location) => (
              <motion.div
                key={location.id}
                variants={fadeInUp}
                className="card-luxury rounded-lg overflow-hidden"
              >
                {/* Colored header */}
                <div className="bg-primary/10 border-b border-primary/20 p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-2xl font-serif text-foreground">{location.name}</h3>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <div className="space-y-3 text-sm text-muted-foreground mb-6">
                    <p className="font-medium text-foreground">{location.address}</p>
                    <p>{location.city}</p>
                    <div className="flex items-center gap-2 pt-2 text-foreground">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="whitespace-pre-line">{location.hours}</span>
                    </div>
                  </div>
                  
                  {/* Phone buttons */}
                  <div className="space-y-3">
                    {location.phones.map((phone, idx) => (
                      <a
                        key={idx}
                        href={`tel:+351${phone.replace(/\s/g, '')}`}
                        className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-lg bg-primary/10 border border-primary/30 text-primary font-medium hover:bg-primary/20 transition-colors duration-300"
                      >
                        <Phone className="w-4 h-4" />
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Maps Section */}
      <section className="section-luxury bg-charcoal relative">
        <div className="container-luxury relative">
          <motion.h2
            initial={{ y: 30 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true }}
            className="text-title font-serif text-foreground text-center mb-12"
          >
            Localizações
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-6">
            {locations.map((location, index) => (
              <motion.div
                key={location.id}
                initial={{ y: 30 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="rounded-lg overflow-hidden border border-border shadow-card"
              >
                <div className="aspect-video">
                  <iframe
                    src={location.mapSrc}
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`Map - ${location.name}`}
                  />
                </div>
                <div className="p-4 bg-card">
                  <h3 className="font-serif text-foreground text-lg mb-1">{location.name}</h3>
                  <p className="text-muted-foreground text-sm">{location.address}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactsPage;
